/* eslint-disable react/prop-types */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Button, Col, Row } from 'react-bootstrap';
import DynamicNormalForm from './dynamicNormalForm';
import { DynamicTableForm } from './dynamicTableForm';
import { generateValidationSchema } from './validationSchema';
import { yupResolver } from '@hookform/resolvers/yup';
import dummy from './dummy';
import DependentFieldsHandler from './DependentFieldsHandler';
import { parse, format, isValid } from 'date-fns';
import { ModalForm } from './dynamicModalForm';

const DynamicForm = ({ data, onSubmit, modalType, header, mode, view }) => {
  const { fields: dataFields, form } = data;
  // const { fields: dataFields, form } = dummy.results;
  const [tableData, setTableData] = useState({});
  const [fields, setFields] = useState(dataFields);
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasDefaultValue, setHasDefaultValue] = useState(mode == 'edit');
  const [modalVisibility, setModalVisibility] = useState({});
  const [modalDataMap, setModalDataMap] = useState({});

  const hideFields = (hideObj, radioValue) => {
    const filteredArr = dataFields.filter(
      field => !hideObj[radioValue]?.includes(field.id)
    );
    setFields(filteredArr);
  };

  useEffect(() => {
    const filterField = fields.filter(field => field.modal);

    if (filterField.length > 0) {
      const modalDataMap = {
        [filterField[0].id]: filterField[0].modal.rows
      };

      setModalDataMap(modalDataMap);
    }
  }, [fields]);

  // Enhanced field visibility logic
  const getFieldVisibility = useCallback((fields, formValues) => {
    const visibilityMap = new Map();

    // Find toggle fields with hide options
    const toggleFields = fields.filter(
      field => field.type === 'toggle' && field.hide && field.hide.length > 0
    );

    toggleFields.forEach(toggleField => {
      // Consider form value first, then fall back to default value
      const toggleValue =
        formValues[toggleField.id] ?? toggleField.defaultValue ?? false;
      const isToggleOn =
        toggleValue === true || toggleValue === 1 || toggleValue === '1';

      // Handle nested array hide configuration
      if (Array.isArray(toggleField.hide[0])) {
        const hideOnToggleOn = toggleField.hide[0];
        const hideOnToggleOff = toggleField.hide[1] || [];

        hideOnToggleOn.forEach(hiddenFieldId => {
          visibilityMap.set(hiddenFieldId, !isToggleOn);
        });

        hideOnToggleOff.forEach(hiddenFieldId => {
          visibilityMap.set(hiddenFieldId, isToggleOn);
        });
      } else {
        // Handle simple array hide configuration
        toggleField.hide.forEach((hiddenFieldId, index) => {
          visibilityMap.set(
            hiddenFieldId,
            isToggleOn ? index === 0 : index === 1
          );
        });
      }
    });

    return visibilityMap;
  }, []);

  // Build dependency chain
  const dependencyChain = useMemo(() => {
    const chain = {};
    fields.forEach(field => {
      if (field.dependsOn) {
        if (!chain[field.dependsOn]) {
          chain[field.dependsOn] = [];
        }
        chain[field.dependsOn].push(field.id);
      }
    });
    return chain;
  }, [fields]);

  // Get all dependent fields recursively
  const getAllDependentFields = useCallback(
    (fieldId, visited = new Set()) => {
      if (visited.has(fieldId)) return [];
      visited.add(fieldId);

      const dependents = dependencyChain[fieldId] || [];
      const allDependents = [...dependents];

      dependents.forEach(depId => {
        const nestedDependents = getAllDependentFields(depId, visited);
        allDependents.push(...nestedDependents);
      });

      return allDependents;
    },
    [dependencyChain]
  );

  const methods = useForm({
    resolver: async (data, context, options) => {
      const formValues = methods.watch();

      // Check if a field should be disabled based on disabling rules
      const getDisabledByField = fieldId => {
        return fields.find(
          f => f.disables?.includes(fieldId) && formValues[f.id]
        )?.id;
      };

      // Get all inactive fields (disabled + their dependents)
      const inactiveFields = new Set();
      fields.forEach(field => {
        const disabledBy = getDisabledByField(field.id);
        if (disabledBy) {
          inactiveFields.add(field.id);
          // Add all dependent fields to inactive set
          getAllDependentFields(field.id).forEach(depId => {
            inactiveFields.add(depId);
          });
        }
      });

      // Filter out inactive fields from validation
      const activeFields = fields.filter(
        field => visibilityMap.get(field.id) !== false
      );

      // Generate schema only for active fields
      const schema = generateValidationSchema({
        fields: activeFields,
        dependencyChain
      });

      return yupResolver(schema)(data, context, options);
    },
    mode: 'all'
  });

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    watch,
    setValue,
    reset
  } = methods;

  // Watch form values
  const formValues = watch();
  // Dynamic field visibility map
  const visibilityMap = useMemo(
    () => getFieldVisibility(fields, formValues),
    [fields, formValues, getFieldVisibility]
  );

  // Initialize modal data from provided data (if in edit mode)
  useEffect(() => {
    if (mode === 'edit' && data.initialValues) {
      // Check if there are modal fields with initial values
      fields
        .filter(field => field.modal)
        .forEach(field => {
          if (data.initialValues[field.id]) {
            setModalDataMap(prev => ({
              ...prev,
              [field.id]: data.initialValues[field.id]
            }));
          }
        });
    }
  }, [data.initialValues, fields, mode]);

  // Handle toggle change with value reset
  const handleToggleChange = (toggleField, newValue) => {
    // Set the toggle value first
    setValue(toggleField.id, newValue);

    // Determine which fields need to be reset based on the new toggle state
    if (toggleField?.hide && Array.isArray(toggleField.hide[0])) {
      const fieldsToReset = newValue
        ? toggleField.hide[0]
        : toggleField.hide[1] || [];

      // Reset the fields to their default values
      fieldsToReset.forEach(fieldId => {
        const field = fields.find(f => f.id === fieldId);
        if (field) {
          setValue(fieldId, field.defaultValue ?? '');
        }
      });
    }
  };

  const handleDateRangeChange = (field, start, end) => {
    if (!start || !end) return;

    // Ensure start date is not after end date
    if (new Date(start) > new Date(end)) {
      console.error('Start date cannot be after end date.');
      return;
    }
    const start_date = format(new Date(start), 'yyyy-MM-dd');
    const end_date = format(new Date(end), 'yyyy-MM-dd');
    // Update form values with selected dates
    setValue(field.id, { start_date, end_date });
  };

  const handleRangeChange = (field, start, end) => {
    if (!start || !end) return;

    // Ensure start date is not after end date
    if (new Date(start) > new Date(end)) {
      console.error('Start date cannot be after end date.');
      return;
    }
    const start_date = `yyyy-${format(new Date(start), 'MM-dd')}`;
    const end_date = `yyyy-${format(new Date(end), 'MM-dd')}`;
    // Update form values with selected dates
    setValue(field.id, { start_date, end_date });
  };
  // Handle row changes for tables
  const handleRowChange = (tableName, updatedRows) => {
    setTableData(prevState => ({
      ...prevState,
      [tableName]: updatedRows
    }));
  };

  const cleanData = data => {
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) => [
        key,
        value === '' ? null : value
      ])
    );
  };

  function formatDatesInObject(obj) {
    const formattedObj = {};

    for (const key of Object.keys(obj)) {
      const value = obj[key];

      // Check if the value is a valid Date object
      if (value instanceof Date && isValid(value)) {
        formattedObj[key] = format(value, 'yyyy-MM-dd'); // Convert to "yyyy-MM-dd"
      } else if (typeof value === 'string') {
        // Attempt to parse if it's a string date
        const parsedDate = parse(
          value,
          "EEE MMM dd yyyy HH:mm:ss 'GMT'XXX",
          new Date()
        );
        if (isValid(parsedDate)) {
          formattedObj[key] = format(parsedDate, 'yyyy-MM-dd');
        } else {
          formattedObj[key] = value; // Keep original value if not a valid date
        }
      } else {
        formattedObj[key] = value;
      }
    }

    return formattedObj;
  }

  // Modify the main submit handler to include modal data
  const onSubmitHandler = async formData => {
    const formattedData = formatDatesInObject(formData);
    if (isProcessing) return;
    setIsProcessing(true);

    try {
      // Filter regular form values
      const filteredFormValues = Object.keys(formattedData).reduce(
        (acc, key) => {
          const field = fields.find(f => f.id === key);
          if (
            field &&
            field.type !== 'sectionHeader' &&
            (!visibilityMap.has(key) || visibilityMap.get(key) === true)
          ) {
            acc[key] = formattedData[key];
          }
          return acc;
        },
        {}
      );

      // Combine regular form values with modal data
      const finalSubmitData = {
        ...filteredFormValues,
        ...modalDataMap // This will add all modal data with their respective IDs as keys
      };

      let finalData = cleanData(finalSubmitData);

      await onSubmit(finalData);
    } catch (error) {
      console.error('Submission error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Render fields with dynamic visibility
  const renderFields = () => {
    return fields
      .filter(
        field =>
          !visibilityMap.has(field.id) || visibilityMap.get(field.id) !== false
      )
      .map(field => {
        if (field.type == 'jsonb') {
          return (
            <Col key={field.id} lg={12}>
              <React.Fragment key={field.id}>
                <div className="text-end mt-3">
                  <Button
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleModal(field.id, true);
                    }}
                    className="mb-3"
                  >
                    Add New
                  </Button>
                </div>
                <ModalForm
                  modalConfig={{
                    ...field.modal,
                    id: field.id // Make sure to pass the field ID to identify the modal data
                  }}
                  onClose={visible => toggleModal(field.id, visible)}
                  show={!!modalVisibility[field.id]}
                  onSubmit={handleModalSubmit}
                  initialData={field.modal.rows}
                />
              </React.Fragment>
            </Col>
          );
        } else {
          return (
            <Col
              lg={
                modalType
                  ? field.type === 'textarea' || field.type === 'radio_group'
                    ? 12
                    : 6
                  : field.type === 'textarea' ||
                    field.type === 'radio_group' ||
                    field.type === 'sectionHeader'
                  ? 12
                  : 4
              }
              key={field.id}
              className="g-lg-4 g-2"
            >
              <DynamicNormalForm
                key={field.id}
                metadata={field}
                control={control}
                mode={mode}
                register={methods.register}
                errors={errors}
                onToggleChange={
                  field.type === 'toggle'
                    ? value => handleToggleChange(field, value)
                    : undefined
                }
                hasDefaultValue={hasDefaultValue}
                setHasDefaultValue={setHasDefaultValue}
                handleDateRangeChange={(start, end) =>
                  handleDateRangeChange(field, start, end)
                }
                handleMonthDayChange={(start, end) =>
                  handleRangeChange(field, start, end)
                }
                modalDataMap={modalDataMap}
                hideFields={hideFields}
              />
            </Col>
          );
        }
      });
  };

  const handleModalSubmit = ({ data, modalId }) => {
    // Store the modal data with its ID as the key
    setModalDataMap(prev => ({
      ...prev,
      [modalId]: data // This will be an array of objects for that specific modal
    }));
  };

  // Toggle modal visibility for a specific modal
  const toggleModal = (modalId, isVisible) => {
    setModalVisibility(prev => ({
      ...prev,
      [modalId]: isVisible
    }));
  };

  return (
    <FormProvider {...methods}>
      <DependentFieldsHandler
        fields={fields}
        watch={methods.watch}
        setValue={methods.setValue}
      />
      <form
        onSubmit={handleSubmit(
          async data => await onSubmitHandler(data),
          errors => {
            console.log('Validation errors:', errors);
          }
        )}
        noValidate
      >
        <Row className="gx-2 gy-3">{renderFields()}</Row>

        {fields
          .filter(field => field.table)
          .map(field => (
            <DynamicTableForm
              key={field.id}
              control={methods.control}
              errors={errors}
              tableConfig={field}
              onRowChange={handleRowChange}
            />
          ))}

        <div className="text-end mt-3">
          {!view && (
            <Button
              type="submit"
              variant="primary"
              size="lg"
              disabled={isSubmitting}
              className="w-100 w-sm-auto"
            >
              {isProcessing || isSubmitting ? 'Submitting...' : 'Submit'}
            </Button>
          )}
        </div>
      </form>
    </FormProvider>
  );
};

export default DynamicForm;
